import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useToast } from '@/components/common/toast';
import { AsyncButton } from '@/components/common/AsyncButton';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { Select2 } from '@/components/forms/Select2';
import { ApiError } from '@/api/client/abpError';
import { usePermission } from '@/permissions/usePermission';
import { tdiInstrumentsApi } from '@/api/services/onboarding';
import {
  CREDIT_QUALITY_STEPS,
  FACE_VALUES,
  type CreateDebtInstrumentDto,
  type DebtInstrumentDto,
  type DebtSymbolFiltersDto,
  type UpdateDebtInstrumentDto,
} from '@/api/types/onboarding';
import { renderNormalNumber } from '../helpers';

const P = 'User.Onboarding.TDIInstruments';

interface Draft {
  issue: string;
  currency: string;
  instrumentType: string; // symbolTypeId
  modifiedDuration: string;
  residualMaturity: string;
  issuerType: string;
  creditQualityStep: string;
  lossGivenDefault: string;
  contractSize: string;
  dataSource: string; // dataSourceId
  digits: string;
  faceValue: string;
}

const emptyDraft = (): Draft => ({
  issue: '',
  currency: '',
  instrumentType: '',
  modifiedDuration: '',
  residualMaturity: '',
  issuerType: '',
  creditQualityStep: '',
  lossGivenDefault: '',
  contractSize: '',
  dataSource: '',
  digits: '',
  faceValue: '100',
});

export function TdiInstrumentsTab() {
  const toast = useToast();
  const can = usePermission();
  const canCreate = can(`${P}.Create`);
  const canUpdate = can(`${P}.Update`);
  const canDelete = can(`${P}.Delete`);
  const canDownloadExcel = can(`${P}.DownloadExcel`);

  const dropdowns = useQuery({
    queryKey: ['onboarding', 'tdi', 'dropdown'],
    queryFn: tdiInstrumentsApi.getDropdownData,
  });
  const opts = dropdowns.data;

  const [newDraft, setNewDraft] = useState<Draft | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DebtInstrumentDto | null>(null);
  const [ddParent, setDdParent] = useState<HTMLElement | null>(null);

  const table = useServerDataTable<DebtInstrumentDto, Partial<DebtSymbolFiltersDto>>({
    queryKey: ['onboarding', 'tdi'],
    fetcher: (req) => tdiInstrumentsApi.getListForDataTable(req as DebtSymbolFiltersDto),
    pageSize: 5,
  });

  const buildCreate = (d: Draft): CreateDebtInstrumentDto => ({
    name: d.issue,
    base: d.issue,
    quote: d.currency,
    symbolTypeId: Number.parseInt(d.instrumentType, 10),
    modifiedDuration: Number.parseFloat(d.modifiedDuration),
    residualMaturity: Number.parseInt(d.residualMaturity, 10),
    issuerType: Number.parseInt(d.issuerType, 10),
    creditQualityStep: Number.parseInt(d.creditQualityStep, 10),
    lossGivenDefault: Number.parseFloat(d.lossGivenDefault),
    contractSize: Number.parseInt(d.contractSize, 10),
    dataSourceId: Number(d.dataSource),
    digits: Number.parseInt(d.digits, 10),
    faceValue: Number.parseFloat(d.faceValue) || 100,
    mifir: false,
  });

  const saveNew = async () => {
    if (!newDraft) return;
    setSaving(true);
    try {
      await tdiInstrumentsApi.create(buildCreate(newDraft));
      toast.success('Added successfully.');
      setNewDraft(null);
      table.refetch();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to create TDI Instrument.');
    } finally {
      setSaving(false);
    }
  };

  const beginEdit = (row: DebtInstrumentDto) => {
    setEditId(row.id);
    setEditDraft({
      issue: row.issue,
      currency: row.currency,
      instrumentType: symbolTypeIdFor(row.instrumentType),
      modifiedDuration: String(row.modifiedDuration),
      residualMaturity: residualIdFor(row.residualMaturity),
      issuerType: issuerIdFor(row.issuerType),
      creditQualityStep: String(row.creditQualityStep),
      lossGivenDefault: String(row.lossGivenDefault),
      contractSize: String(row.contractSize),
      dataSource: dataSourceIdFor(row.dataSource),
      digits: String(row.digits),
      // The list DTO (DebtInstrumentDto) does not expose FaceValue, so the edit
      // dropdown can't pre-select the row's current value; default to the first
      // option. It is fully editable and persisted on save.
      faceValue: String(FACE_VALUES[0]),
    });
  };

  // Resolve display text back to the numeric/id values for editing.
  function symbolTypeIdFor(name: string): string {
    return String(opts?.symbolTypes.find((s) => s.name === name)?.id ?? '');
  }
  function dataSourceIdFor(name: string): string {
    return String(opts?.dataSources.find((s) => s.name === name)?.id ?? '');
  }
  function residualIdFor(name: string): string {
    return String(opts?.residualMaturityOptions.find((s) => s.name === name)?.id ?? '');
  }
  function issuerIdFor(name: string): string {
    return String(opts?.issuerTypeOptions.find((s) => s.name === name)?.id ?? '');
  }

  const saveEdit = async () => {
    if (!editId || !editDraft) return;
    setSaving(true);
    try {
      const dto: UpdateDebtInstrumentDto = {
        id: editId,
        base: editDraft.issue,
        quote: editDraft.currency,
        symbolTypeId: Number.parseInt(editDraft.instrumentType, 10),
        modifiedDuration: Number.parseFloat(editDraft.modifiedDuration),
        residualMaturity: Number.parseInt(editDraft.residualMaturity, 10),
        issuerType: Number.parseInt(editDraft.issuerType, 10),
        creditQualityStep: Number.parseInt(editDraft.creditQualityStep, 10),
        lossGivenDefault: Number.parseFloat(editDraft.lossGivenDefault),
        contractSize: Number.parseInt(editDraft.contractSize, 10),
        dataSourceId: Number(editDraft.dataSource),
        digits: Number.parseInt(editDraft.digits, 10),
        faceValue: Number.parseFloat(editDraft.faceValue) || 100,
        mifir: false,
      };
      await tdiInstrumentsApi.update(editId, dto);
      toast.success('Saved successfully.');
      setEditId(null);
      setEditDraft(null);
      table.refetch();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to update TDI Instrument.');
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await tdiInstrumentsApi.delete(deleteTarget.id);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to delete.');
    } finally {
      setDeleteTarget(null);
      table.refetch();
    }
  };

  const sel = (d: Draft | null, k: keyof Draft, set: (d: Draft) => void, options: { value: string | number; label: string }[]) =>
    d && (
      <Select2
        value={d[k]}
        options={options}
        placeholder="N/A"
        allowClear={false}
        searchable={false}
        disabled={saving}
        dropdownParent={ddParent}
        onChange={(v) => set({ ...d, [k]: v ?? '' })}
      />
    );

  const txt = (d: Draft | null, k: keyof Draft, set: (d: Draft) => void, type = 'text') =>
    d && <input type={type} className="form-control form-control-sm" value={d[k]} disabled={saving} onChange={(e) => set({ ...d, [k]: e.target.value })} />;

  const faceValueOpts = FACE_VALUES.map((f) => ({ value: f, label: String(f) }));

  const symbolTypeOpts = (opts?.symbolTypes ?? []).map((s) => ({ value: s.id, label: s.name }));
  const currencyOpts = (opts?.currencies ?? []).map((c) => ({ value: c.name, label: c.name }));
  const residualOpts = (opts?.residualMaturityOptions ?? []).map((s) => ({ value: s.id, label: s.name }));
  const issuerOpts = (opts?.issuerTypeOptions ?? []).map((s) => ({ value: s.id, label: s.name }));
  const stepOpts = (opts?.creditQualitySteps ?? [...CREDIT_QUALITY_STEPS]).map((n) => ({ value: n, label: String(n) }));
  const dsOpts = (opts?.dataSources ?? []).map((s) => ({ value: s.id, label: s.name }));

  const columns = useMemo<DataGridColumn<DebtInstrumentDto>[]>(
    () => [
      {
        key: 'issue', title: 'Issue', className: 'text-start', value: (r) => r.issue,
        cell: (r) => (editId === r.id ? r.issue /* read-only on edit */ : r.issue),
      },
      {
        key: 'currency', title: 'Currency', className: 'text-start', value: (r) => r.currency,
        cell: (r) => (editId === r.id ? sel(editDraft, 'currency', setEditDraft, currencyOpts) : r.currency),
      },
      {
        key: 'instrumentType', title: 'Instrument Type', className: 'text-start', value: (r) => r.instrumentType,
        cell: (r) => (editId === r.id ? sel(editDraft, 'instrumentType', setEditDraft, symbolTypeOpts) : r.instrumentType),
      },
      {
        key: 'modifiedDuration', title: 'Modified Duration', className: 'text-end', value: (r) => r.modifiedDuration,
        cell: (r) => (editId === r.id ? txt(editDraft, 'modifiedDuration', setEditDraft, 'number') : r.modifiedDuration),
      },
      {
        key: 'residualMaturity', title: 'Residual Maturity', className: 'text-start', value: (r) => r.residualMaturity,
        cell: (r) => (editId === r.id ? sel(editDraft, 'residualMaturity', setEditDraft, residualOpts) : r.residualMaturity),
      },
      {
        key: 'issuerType', title: 'Issuer Type', className: 'text-start', value: (r) => r.issuerType,
        cell: (r) => (editId === r.id ? sel(editDraft, 'issuerType', setEditDraft, issuerOpts) : r.issuerType),
      },
      {
        key: 'creditQualityStep', title: 'Credit Quality Step', className: 'text-end', value: (r) => r.creditQualityStep,
        cell: (r) => (editId === r.id ? sel(editDraft, 'creditQualityStep', setEditDraft, stepOpts) : r.creditQualityStep),
      },
      {
        key: 'lossGivenDefault', title: 'Loss Given Default', className: 'text-end', value: (r) => r.lossGivenDefault,
        cell: (r) => (editId === r.id ? txt(editDraft, 'lossGivenDefault', setEditDraft, 'number') : r.lossGivenDefault),
      },
      {
        key: 'contractSize', title: 'Contract Size', className: 'text-end', value: (r) => r.contractSize,
        cell: (r) => (editId === r.id ? txt(editDraft, 'contractSize', setEditDraft, 'number') : renderNormalNumber(r.contractSize)),
      },
      {
        key: 'dataSource', title: 'Connector', className: 'text-start', value: (r) => r.dataSource,
        cell: (r) => (editId === r.id ? r.dataSource /* read-only on edit */ : r.dataSource),
      },
      {
        key: 'digits', title: 'Digits', className: 'text-end', filterable: false, value: (r) => r.digits,
        cell: (r) =>
          editId === r.id ? (
            <div className="d-flex flex-column gap-1">
              {txt(editDraft, 'digits', setEditDraft, 'number')}
              {editDraft && (
                <Select2
                  value={editDraft.faceValue}
                  options={faceValueOpts}
                  placeholder="N/A"
                  allowClear={false}
                  searchable={false}
                  disabled={saving}
                  dropdownParent={ddParent}
                  onChange={(v) => setEditDraft({ ...editDraft, faceValue: v ?? '' })}
                />
              )}
            </div>
          ) : (
            r.digits
          ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editId, editDraft, saving, opts],
  );

  const toolbar = canCreate ? (
    <AsyncButton
      className="btn btn-primary btn-sm"
      icon="fa fa-plus"
      title="Create new TDI Instrument"
      onClick={() => { if (!newDraft) setNewDraft(emptyDraft()); }}
    >
      Create New
    </AsyncButton>
  ) : null;

  const footerTop = newDraft ? (
    <tr className="onb-new-row">
      <td>{txt(newDraft, 'issue', setNewDraft)}</td>
      <td>{sel(newDraft, 'currency', setNewDraft, currencyOpts)}</td>
      <td>{sel(newDraft, 'instrumentType', setNewDraft, symbolTypeOpts)}</td>
      <td>{txt(newDraft, 'modifiedDuration', setNewDraft, 'number')}</td>
      <td>{sel(newDraft, 'residualMaturity', setNewDraft, residualOpts)}</td>
      <td>{sel(newDraft, 'issuerType', setNewDraft, issuerOpts)}</td>
      <td>{sel(newDraft, 'creditQualityStep', setNewDraft, stepOpts)}</td>
      <td>{txt(newDraft, 'lossGivenDefault', setNewDraft, 'number')}</td>
      <td>{txt(newDraft, 'contractSize', setNewDraft, 'number')}</td>
      <td>{sel(newDraft, 'dataSource', setNewDraft, dsOpts)}</td>
      <td>
        <div className="d-flex flex-column gap-1">
          {txt(newDraft, 'digits', setNewDraft, 'number')}
          <Select2
            value={newDraft.faceValue}
            options={faceValueOpts}
            placeholder="N/A"
            allowClear={false}
            searchable={false}
            disabled={saving}
            dropdownParent={ddParent}
            onChange={(v) => setNewDraft({ ...newDraft, faceValue: v ?? '' })}
          />
        </div>
      </td>
      <td>
        <div className="d-flex gap-1">
          <AsyncButton className="btn btn-sm btn-outline-primary" icon="fa fa-check" title="Save" onClick={saveNew} disabled={saving} />
          <button type="button" className="btn btn-sm btn-outline-danger" title="Cancel" disabled={saving} onClick={() => setNewDraft(null)}><i className="fa fa-times" /></button>
        </div>
      </td>
    </tr>
  ) : undefined;

  const rowActions = (canUpdate || canDelete)
    ? (row: DebtInstrumentDto) => (
        <div className="d-flex gap-1">
          {canUpdate && editId !== row.id && (
            <button type="button" className="btn btn-sm btn-outline-primary" title="Edit" onClick={() => beginEdit(row)}>
              <i className="fa fa-pencil" />
            </button>
          )}
          {editId === row.id && (
            <>
              <AsyncButton className="btn btn-sm btn-outline-primary" icon="fa fa-check" title="Save" onClick={saveEdit} disabled={saving} />
              <button type="button" className="btn btn-sm btn-outline-danger" title="Cancel" disabled={saving} onClick={() => { setEditId(null); setEditDraft(null); }}><i className="fa fa-times" /></button>
            </>
          )}
          {canDelete && editId !== row.id && (
            <button type="button" className="btn btn-sm btn-outline-danger" title="Delete" onClick={() => setDeleteTarget(row)}>
              <i className="fa fa-trash" />
            </button>
          )}
        </div>
      )
    : undefined;

  return (
    <div ref={setDdParent}>
      <DataGrid
        columns={columns}
        data={table.rows}
        rowKey={(r) => r.id}
        card
        columnSearch
        columnSearchValues={table.columnFilters}
        onColumnSearchChange={table.setColumnFilter}
        toolbarStart={toolbar}
        includeExcel={canDownloadExcel}
        excelFileName="Debt Symbols"
        footerTop={footerTop}
        rowActions={rowActions}
        rowClassName={(r) => (editId === r.id ? 'sfc-detail-editing' : undefined)}
        emptyMessage={table.isLoading ? 'Loading…' : 'No data available'}
        server={{
          pageIndex: table.pageIndex,
          pageSize: table.pageSize,
          pageCount: table.pageCount,
          totalCount: table.totalCount,
          onPageChange: table.setPageIndex,
          onPageSizeChange: table.setPageSize,
        }}
      />

      <ConfirmDialog
        show={deleteTarget != null}
        message={deleteTarget ? `Delete TDI Instrument '${deleteTarget.issue}'?` : ''}
        confirmLabel="Delete"
        onConfirm={doDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
